/*
Main page for example use cases. Fully data-driven off the backend
/list_examples endpoint (see store/functions/exampleCatalog.js). Renders one
card per published example, with faceted filtering (see ./exampleFilters).

This is now the only way to browse examples — the sidebar used to list every
one, which stopped scaling past a few dozen.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { Box, Button, CircularProgress, Container, Grid, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import CardUseCase from "components/domain/use-cases/CardUseCase.js";
import ExampleFilterBar from "components/domain/use-cases/ExampleFilterBar.jsx";
import {
  CONSTRAINTS_FACET_KEY,
  emptySelections,
  filterExamples,
  searchParamsToSelections,
  selectionsToSearchParams,
} from "components/domain/use-cases/exampleFilters";
import {
  selectExampleCatalog,
} from "store/reducers/uiReducer";
import { useEnsureExampleCatalog } from "store/functions/exampleCatalog.js";
import useSeo from "utils/useSeo";
import { pageDescription } from "utils/routeMeta";
import { fetchExampleList } from "store/reducers/thunks/scenarioThunks";


function UseCasesPage() {
  useSeo({
    title: "Examples",
    description: pageDescription('/use-cases'),
    canonicalPath: "/use-cases",
  });
  const description = "Real scenarios you can open, change, and run. Start from one close to your operation instead of a blank page.";

  useEnsureExampleCatalog();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { items: examples, status, error } = useSelector(selectExampleCatalog);

  // Filter state is seeded from (and written back to) the query string, so a
  // filtered view is shareable and back/forward work.
  const initial = useMemo(() => searchParamsToSelections(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [selections, setSelections] = useState(initial.selections);
  const [query, setQuery] = useState(initial.query);

  useEffect(() => {
    const params = selectionsToSearchParams(selections, query);
    const search = params.toString();
    const next = search ? `?${search}` : "";
    if (next !== location.search) {
      history.replace({ pathname: location.pathname, search: next });
    }
  }, [selections, query, history, location.pathname, location.search]);

  const filteredExamples = useMemo(
    () => filterExamples(examples, selections, query),
    [examples, selections, query]
  );

  const toggleFacetValue = useCallback((facetKey, value) => {
    setSelections((prev) => {
      const current = prev[facetKey] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [facetKey]: next };
    });
  }, []);

  const clearAll = useCallback(() => {
    setSelections(emptySelections());
    setQuery("");
  }, []);

  const isLoading = status === "loading" || status === "idle";
  const isError = status === "failed";

  return (
    <Box sx={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 0.5, md: 1.5 },
          pb: { xs: 1.5, md: 2.5 },
          background: "linear-gradient(180deg, rgba(230, 252, 251, 0.5) 0%, #FFFFFF 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center", mb: 1.5 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "1.5rem", md: "1.875rem" },
                fontWeight: 700,
                color: "dark.main",
                lineHeight: 1.15,
                mb: 1,
              }}
            >
              Examples
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "0.9rem",
                color: "text.secondary",
                maxWidth: "520px",
                mx: "auto",
                mb: 2,
                lineHeight: 1.5,
              }}
            >
              {description}
            </Typography>

            <ExampleFilterBar
              examples={examples}
              selections={selections}
              query={query}
              onToggle={toggleFacetValue}
              onQueryChange={setQuery}
              onClearAll={clearAll}
              resultCount={filteredExamples.length}
            />
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 2 }}>
        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
              We couldn't load the examples right now.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {error || "Please try again in a moment."}
            </Typography>
            <Button
              variant="contained"
              onClick={() => dispatch(fetchExampleList())}
              sx={{ mt: 2 }}
            >
              Try again
            </Button>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filteredExamples.map((example, idx) => (
              <Grid item xs={12} md={6} key={example.slug || idx}>
                <CardUseCase
                  example={example}
                  selectedTags={selections[CONSTRAINTS_FACET_KEY] || []}
                  onToggleTag={(tag) => toggleFacetValue(CONSTRAINTS_FACET_KEY, tag)}
                />
              </Grid>
            ))}
            {filteredExamples.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 10 }}>
                  <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                    {examples.length === 0
                      ? "No examples are available yet. Check back soon."
                      : "No examples match these filters."}
                  </Typography>
                  {examples.length > 0 && (
                    <Button onClick={clearAll} sx={{ mt: 2 }}>
                      Clear all filters
                    </Button>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </Box>
  );
}

export default UseCasesPage;
